/**
 * Read display rotation from MP4/QuickTime track header (tkhd) matrix.
 * Browsers (esp. Chromium) often ignore this metadata for .MOV, so callers
 * can apply an equivalent CSS/canvas transform for correct playback.
 */

export type VideoDisplayRotationDegrees = 0 | 90 | 180 | 270;

const FIXED_ONE = 0x00010000;
const FIXED_NEG_ONE = 0xffff0000;

const CONTAINER_ATOMS = new Set([
  'moov',
  'trak',
  'mdia',
  'minf',
  'stbl',
  'edts',
  'moof',
  'traf',
]);

function readUInt32BE(bytes: Uint8Array, offset: number): number {
  return (
    ((bytes[offset]! << 24)
      | (bytes[offset + 1]! << 16)
      | (bytes[offset + 2]! << 8)
      | bytes[offset + 3]!)
    >>> 0
  );
}

function atomType(bytes: Uint8Array, offset: number): string {
  return String.fromCharCode(
    bytes[offset]!,
    bytes[offset + 1]!,
    bytes[offset + 2]!,
    bytes[offset + 3]!,
  );
}

function matrixToDegrees(a: number, b: number, c: number, d: number): VideoDisplayRotationDegrees | null {
  const near = (value: number, expected: number) => (
    value === expected
    || value === ((expected + 1) >>> 0)
    || value === ((expected - 1) >>> 0)
  );

  if (near(a, FIXED_ONE) && near(b, 0) && near(c, 0) && near(d, FIXED_ONE)) {
    return 0;
  }
  // 90° clockwise
  if (near(a, 0) && near(b, FIXED_ONE) && near(c, FIXED_NEG_ONE) && near(d, 0)) {
    return 90;
  }
  // 180°
  if (near(a, FIXED_NEG_ONE) && near(b, 0) && near(c, 0) && near(d, FIXED_NEG_ONE)) {
    return 180;
  }
  // 270° clockwise
  if (near(a, 0) && near(b, FIXED_NEG_ONE) && near(c, FIXED_ONE) && near(d, 0)) {
    return 270;
  }
  return null;
}

function rotationFromTkhd(payload: Uint8Array): VideoDisplayRotationDegrees | null {
  if (payload.length < 4) return null;
  const version = payload[0]!;
  // version 0: matrix at 48; version 1: matrix at 60
  const matrixOffset = version === 1 ? 60 : 48;
  if (payload.length < matrixOffset + 16) return null;

  const a = readUInt32BE(payload, matrixOffset);
  const b = readUInt32BE(payload, matrixOffset + 4);
  const c = readUInt32BE(payload, matrixOffset + 8);
  const d = readUInt32BE(payload, matrixOffset + 12);
  return matrixToDegrees(a, b, c, d);
}

function walkAtoms(
  bytes: Uint8Array,
  start: number,
  end: number,
  onTkhd: (rotation: VideoDisplayRotationDegrees) => boolean,
): boolean {
  let offset = start;

  while (offset + 8 <= end) {
    let size = readUInt32BE(bytes, offset);
    const type = atomType(bytes, offset + 4);
    let headerSize = 8;

    if (size === 1) {
      if (offset + 16 > end) return false;
      const high = readUInt32BE(bytes, offset + 8);
      const low = readUInt32BE(bytes, offset + 12);
      if (high !== 0) return false;
      size = low;
      headerSize = 16;
    } else if (size === 0) {
      size = end - offset;
    }

    if (size < headerSize || offset + size > end) {
      return false;
    }

    const payloadStart = offset + headerSize;
    const payloadEnd = offset + size;

    if (type === 'tkhd') {
      const rotation = rotationFromTkhd(bytes.subarray(payloadStart, payloadEnd));
      if (rotation != null && onTkhd(rotation)) {
        return true;
      }
    } else if (CONTAINER_ATOMS.has(type)) {
      if (walkAtoms(bytes, payloadStart, payloadEnd, onTkhd)) {
        return true;
      }
    }

    offset = payloadEnd;
  }

  return false;
}

/**
 * Scan a buffer (full file, or concatenated head+tail slices) for video-track
 * tkhd matrices. Prefers the first non-zero rotation; otherwise returns 0.
 */
export function parseVideoDisplayRotationDegrees(
  bytes: Uint8Array,
): VideoDisplayRotationDegrees {
  let foundIdentity = false;
  let nonZero: VideoDisplayRotationDegrees | null = null;

  const consider = (rotation: VideoDisplayRotationDegrees): boolean => {
    if (rotation === 0) {
      foundIdentity = true;
      return false;
    }
    nonZero = rotation;
    return true;
  };

  // Primary: structured atom walk from start
  walkAtoms(bytes, 0, bytes.length, consider);
  if (nonZero != null) return nonZero;

  // Fallback: brute-force scan for 'tkhd' markers (handles head||tail concat
  // where atom sizes are truncated at the join).
  const marker = [0x74, 0x6b, 0x68, 0x64]; // tkhd
  for (let i = 0; i + 8 < bytes.length; i += 1) {
    if (
      bytes[i] === marker[0]
      && bytes[i + 1] === marker[1]
      && bytes[i + 2] === marker[2]
      && bytes[i + 3] === marker[3]
    ) {
      const payload = bytes.subarray(i + 4, Math.min(bytes.length, i + 4 + 96));
      const rotation = rotationFromTkhd(payload);
      if (rotation === 0) {
        foundIdentity = true;
      } else if (rotation != null) {
        return rotation;
      }
    }
  }

  return foundIdentity || nonZero == null ? (nonZero ?? 0) : nonZero;
}

export function normalizeVideoDisplayRotationDegrees(
  degrees: number | undefined | null,
): VideoDisplayRotationDegrees {
  const normalized = ((((degrees ?? 0) % 360) + 360) % 360);
  switch (normalized) {
    case 90:
      return 90;
    case 180:
      return 180;
    case 270:
      return 270;
    default:
      return 0;
  }
}
